import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom'
import moment from "moment";
import useApi from '../../../utility/Hooks/Api/useApi';
import { IdentificationIcon, PencilSquareIcon, SquaresPlusIcon } from '@heroicons/react/24/solid'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBarcode, faFloppyDisk, faRectangleXmark } from "@fortawesome/free-solid-svg-icons";
import { faSquarePlus } from "@fortawesome/free-regular-svg-icons";
import { ElectronicHousingUnitApi } from '../../../services/Api/ElectronicHousingUnitApi';
import { CompanyApi } from '../../../services/Api/CompanyApi';
import { UsersApi } from '../../../services/Api/UsersApi';
import { PartSpecificationsApi } from '../../../services/Api/PartSpecificationsApi';
import RemarkTextArea from "../../../components/Inputs/Remark/RemarkTextArea";
import PartsSelect from "../../../components/Selects/Parts/PartsSelect";
import CustomSelect from "../../../components/Selects/Custom/CustomSelect";
import { MinusCircleIcon } from "@heroicons/react/24/outline";
import DetailFormDatePicker from '../../../components/Selects/DatePicker/DetailFormDatePicker';

export default function ElectronicHousingUnitDetailView() {
    const { electronicHousingUnitId: id } = useParams();
    const { request: ehuRequest, data: ehuData, loading: ehuLoading } = useApi();
    const { request: ehuPartsRequest, data: ehuPartsData, set: ehuSetPartsData, loading: ehuPartsLoading } = useApi();
    const { request: ehuVehicleRequest, data: ehuVehicleData, set: ehuSetVehicleData, loading: ehuVehicleLoading } = useApi();
    const { request: companyRequest, data: companyData, loading: companyLoading } = useApi();
    const { request: specRequest, data: specData, loading: specLoading } = useApi();
    const { request: linkRequest, data: linkData, loading: linkLoading } = useApi();
    const { request: ehuRemarkRequest, data: ehuRemarkData, loading: ehuRemarkLoading } = useApi();
    const { request: accountRequest, data: accountData, loading: accountLoading } = useApi();
    const [newRemark, setNewRemark] = useState("");
    const [editRemark, setEditRemark] = useState({ id: null, text: "" });
    const [showSelect, setShowSelect] = useState(false);
    const [editing, setEditing] = useState({
        show: false,
        assembledBy: false,
        assemblyDate: false,
        installedBy: false,
        installationDate: false,
        certificateNumber: false,
    });

    const [editableEhuData, setEditableEhuData] = useState({});
    const [loading, setLoading] = useState(true);
    const [partError, setPartError] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            try {
                await accountRequest(UsersApi.CurrentUser())
                await ehuRequest(ElectronicHousingUnitApi.getElectronicHousingUnit(id));
                await ehuPartsRequest(ElectronicHousingUnitApi.getElectronicHousingUnitParts(id));
                await ehuVehicleRequest(ElectronicHousingUnitApi.getElectronicHousingUnitVehicles(id));
                await companyRequest(CompanyApi.getCompanies());
                await specRequest(PartSpecificationsApi.getPartSpecifications())
            } catch (error) {

            } finally {
                setLoading(false);
            };
        };

        fetchData();
    }, []);

    const certificateHandler = (e) => {
        const certificate = e?.find(e => e.text.includes("certificateNumber"))?.["text"];
        return certificate ? certificate.split(": ").pop() : ehuData["certificate"]
    };

    useEffect(() => {
        if (ehuData) {
            // First check if certificate is already present in ehuData (base object)
            if (ehuData.certificate) {
                setEditableEhuData(prevData => ({
                    ...ehuData,
                    certificate: ehuData.certificate, // Use certificate from base object
                }));
            } else {
                // If no certificate in base object, check if there's one in remarks
                const certificateFromRemarks = certificateHandler(ehuData["remarks"]);

                // If certificate exists in remarks, update the editableEhuData's certificate field
                if (certificateFromRemarks) {
                    setEditableEhuData(prevData => ({
                        ...ehuData,
                        certificate: certificateFromRemarks, // Set certificate from remarks if available
                    }));
                } else {
                    // If no certificate in either place, just set editableEhuData normally
                    setEditableEhuData(prevData => ({
                        ...ehuData,
                        certificate: "", // Or any default value you want if no certificate is found
                    }));
                }
            }
        }

    }, [ehuData]);

    const newRemarkHandler = (e) => {
        if (editRemark.id) {
            setEditRemark({ ...editRemark, text: e.target.value })
        } else {
            setNewRemark(e.target.value)
        }
    };

    const newRemarkRequestHandler = async (e) => {

        if (editRemark.id && editRemark.text !== "") {
            const obj = {
                "id": editRemark.id,
                "remark": editRemark.text.toString()
            };

            try {
                await ehuRemarkRequest(ElectronicHousingUnitApi.editRemark(obj.id, obj.remark));
                await ehuRequest(ElectronicHousingUnitApi.getElectronicHousingUnit(id));
                setEditRemark({ id: null, text: "" })
            } catch (error) {

            }

        } else {
            if (newRemark !== "") {
                const obj = {
                    "remark": newRemark.toString()
                };
                try {
                    await ehuRequest(ElectronicHousingUnitApi.remarkElectronicHousingUnit(id, obj));
                    await ehuRequest(ElectronicHousingUnitApi.getElectronicHousingUnit(id));
                } catch (error) {

                }
                setNewRemark("")
            }
        }
    };

    const newRemarkCancelHandler = (e) => {
        if (editRemark.id) {
            setEditRemark({ id: null, text: "" })
        } else {
            setNewRemark("")
        }
    };

    const dataUpdateRequestHandler = (e) => {
        const { id, ...dataToSend } = e;
        ehuRequest(ElectronicHousingUnitApi.updateElectronicHousingUnit(id, dataToSend));
    }

    const reverseArray = (arr) => {
        return arr?.slice().reverse()
    };

    const addPart = async (e) => {
        const part = JSON.parse(e.value);
        const serialNumber = part.serialNumber
        const type = part.specification.PartType
        try {
            await linkRequest(ElectronicHousingUnitApi.addPart(id, part.id));
            ehuPartsData.push(part);
            if (partError) { setPartError(null) }
        } catch (error) {
            if (error?.response?.data.includes("already has a connected Electronic Housing Unit")) {
                setPartError({ serialNumber, type, partId: part.id });
            }
        } finally {
            setShowSelect(false)
        }
    };

    const removePart = async (partId) => {
        await linkRequest(ElectronicHousingUnitApi.removePart(id, partId));
        const newArr = ehuPartsData.filter(part => {
            return part.id !== partId;
        });
        ehuSetPartsData([...newArr]);
    };

    // Handle input change
    const handleInputChange = (field, value) => {
        setEditableEhuData(prevState => ({
            ...prevState,
            [field]: value,
        }));
    };

    const handleRemarkDelete = async (remarkId) => {
        try {
            await ehuRemarkRequest(ElectronicHousingUnitApi.deleteRemark(remarkId));
            await ehuRequest(ElectronicHousingUnitApi.getElectronicHousingUnit(id));
        } catch (error) {

        }

    }

    const handleRemarkEdit = async (id, text) => {
        setEditRemark({ id, text });
    }

    // Function to toggle editing state
    const toggleEditing = (field) => {
        setEditing(prevState => ({
            ...prevState,
            [field]: !prevState[field],
        }));
    };

    const formatCertificateNumber = (certificate, isEditing) => {
        const prefix = "ImEfficiencySOT000000";

        // If the user is editing, do not apply the prefix (no formatting)
        if (isEditing) {
            return certificate;
        }

        // If the certificate is empty, return it as is (no prefix added when empty)
        if (!certificate) {
            return certificate;
        }

        if (certificate === "0") {
            return "";
        };

        // If the certificate starts with the prefix, return it as is
        if (certificate.startsWith(prefix)) {
            return certificate;
        }

        // Otherwise, add the prefix to the certificate
        return `${prefix}${certificate}`;
    };

    const companyOptions = () => {
        return companyData.map((company) => ({ value: company.id, label: company["company name"] }));
    };

    const getCompanyNameById = (companyId) => {
        return companyData?.find(company => company.id === companyId)?.["company name"] || "Unknown Company";
    };

    const getCaseInsensitive = (obj, key) => {
        if (!obj) return undefined;
        const lowerCaseKey = key.toLowerCase();
        return Object.keys(obj).reduce((result, currentKey) => {
            return currentKey.toLowerCase() === lowerCaseKey ? obj[currentKey] : result;
        }, undefined);
    };

    const typeOptions = [
        { value: 1, label: 'Classic Truck-Trailer' },
        { value: 2, label: 'Classic Truck Rigid' },
        { value: 3, label: 'Classic Double Decker Stand-alone' },
        { value: 4, label: 'Classic Trailer Stand-alone' },
        { value: 5, label: 'Classic Custom' },
        { value: 6, label: 'Lite Smart' },
    ];

    // from here is some new code for testing, can be deleted afterwards

    const EditableField = ({ label, value, isEditing, onEdit, options, onSave, type = "text", backgroundColor, field, route, disabled }) => {

        const handleClick = () => {
            if (route) navigate(`${route}`);
        };

        return (
            <div className={`flex py-2 border-t border-blue-100 px-4 text-sm ${backgroundColor}`}>
                <div className="w-1/2 font-semibold">{label}</div>
                <div className="w-1/2">
                    {isEditing ? (
                        options ? (
                            <CustomSelect
                                options={options}
                                placeHolder={`Select company...`}
                                onChange={(e) => onEdit((field === "productType") ? e.label : e.value)}
                                isSearchable
                                value={value}
                            />
                        ) : field === "assemblyDate" || field === "installationDate" ? (
                            <DetailFormDatePicker
                                label={label}
                                type={type}
                                onChange={(date) => onEdit(date)}
                                value={value ? new Date(value) : null} // Set the initial date value
                            />
                        ) : (
                            <input
                                type={type}
                                className="border border-gray-200 px-2 py-1 rounded w-full"
                                value={value || (editing.show ? '' : 'N/A')}
                                onChange={(e) => onEdit(e.target.value)}
                                autoFocus
                                disabled={disabled}
                            />
                        )
                    ) : (
                        <span className={`text-gray-700 ${route ? 'cursor-pointer hover:underline' : ''}`}
                            onClick={route ? handleClick : undefined}>
                            {(field === "assemblyDate" || field === "installationDate" ? value ? moment(value).format("LL").toString() : "N/A" : value) || (editing.show ? '' : 'N/A')}
                        </span>
                    )}
                </div>
            </div>
        );
    };


    const EditableFieldsList = ({
        editableData,
        onEditField,
        onSaveField,
        editing,
        toggleEditing,
    }) => {
        const vehicle = ehuVehicleData?.[0];
        return (
            <div className="border border-blue-100 rounded w-full">
                {/* Column headers */}
                <div className="flex py-2 px-4 font-bold text-gray-800 bg-blue-100/75 text-sm">
                    <div className="w-1/2">Field</div>
                    <div className="w-1/2">Value</div>
                </div>
                {/* List of fields */}
                {[
                    { label: "EHU Type", value: editableData.typeCode, field: "typeCode" },
                    { label: "Serial Number", value: editableData.productCode, field: "productCode" },
                    { label: "SoT Type", value: editableData.productType, field: "productType", options: typeOptions },
                    { label: "Owner", value: getCompanyNameById(editableData.customerId), field: "customerId", options: companyOptions(), route: `/main/company/${editableData.customerId}` },
                    { label: "Vehicle", value: getCaseInsensitive(vehicle, "reference"), field: "vehicle", route: `/main/vehicle/${ehuVehicleData[0]?.id}`, disabled: true },
                    { label: "Assembly Company", value: getCompanyNameById(editableData.assemblyCompanyId), field: "assemblyCompanyId", options: companyOptions(), route: `/main/company/${editableData.assemblyCompanyId}` },
                    { label: "Assembly Date", value: editableData.assemblyDate, field: "assemblyDate" },
                    { label: "Assembled By", value: editableData.assembledBy, field: "assembledBy" },
                    { label: "Installation Company", value: getCompanyNameById(editableData.installationCompanyId), field: "installationCompanyId", options: companyOptions(), route: editableData.installationCompanyId ? `/main/company/${editableData.installationCompanyId}` : null },
                    { label: "Installation Date", value: editableData.installationDate, field: "installationDate" },
                    { label: "Installed By", value: editableData.installedBy, field: "installedBy" },
                    { label: "Certificate", value: formatCertificateNumber(editableData.certificate, editing.show), field: "certificate" },
                ].map((item, index) => (
                    <EditableField
                        key={item.field}
                        label={item.label}
                        value={item.value}
                        isEditing={editing.show}
                        onEdit={(value) => onEditField(item.field, value)}
                        field={item.field}
                        options={item.options}
                        backgroundColor={index % 2 === 0 ? 'bg-white' : 'bg-blue-100/20'}
                        route={item.route}
                        disabled={item.disabled}
                    />
                ))}
            </div>
        );
    };

    const InfoSection = ({
        editableData,
        companyData,
        companyOptions,
        toggleEditing,
        editing,
    }) => {
        const [editableEhuData, setEditableEhuData] = useState({ ...editableData });
        const [saving, setSaving] = useState({
            info: false,
        })

        // Handle input changes by updating editableEhuData state immutably
        const handleEditField = (field, value) => {
            setEditableEhuData((prevState) => ({
                ...prevState,
                [field]: value,
            }));
        };

        const handleSaveField = async () => {
            setSaving((prevState) => ({
                ...prevState,
                info: true, // Set specific saving status to true
            }));
            try {
                await new Promise((resolve) => setTimeout(resolve, 300)); // Small delay to show "saving"
                await dataUpdateRequestHandler(editableEhuData); // Actual save operation
            } finally {
                setSaving((prevState) => ({
                    ...prevState,
                    info: false, // Set specific saving status to true
                }));
                toggleEditing('show'); // Exit editing mode after saving
            }
        };

        return (
            <div className="w-full px-4">
                <div className="flex items-center justify-between border-b-2 border-gray-100 mt-2 mb-4">
                    <h3 className="font-semibold text-lg text-gray-800">System Information</h3>
                    {!editing.show ? (
                        <PencilSquareIcon
                            className="h-5 w-5 text-gray-600 hover:text-blue-400 cursor-pointer"
                            onClick={() => toggleEditing('show')}
                        />
                    ) : (
                        <div className="flex space-x-2 items-center">
                            {saving.info ? (
                                <span className="text-sm text-indigo-600">Saving changes...</span>
                            ) : (
                                <>
                                    <FontAwesomeIcon
                                        icon={faFloppyDisk}
                                        className="h-5 w-5 text-gray-600 hover:text-green-400 cursor-pointer"
                                        onClick={handleSaveField}
                                    />
                                    <FontAwesomeIcon
                                        icon={faRectangleXmark}
                                        className="h-5 w-5 text-gray-600 hover:text-red-400 cursor-pointer"
                                        onClick={() => toggleEditing('show')}
                                    />
                                </>
                            )}
                        </div>
                    )}
                </div>
                <EditableFieldsList
                    editableData={editableEhuData}
                    onEditField={handleEditField}
                    onSaveField={handleSaveField}
                    editing={editing}
                    toggleEditing={toggleEditing}
                />
            </div>
        );
    };


    return (
        <>
            {!loading ?
                <div className="relative container mx-auto bg-white rounded-lg shadow-2xl min-h-[46.4rem]">
                    <div className="mb-1 sm:mb-4 justify-between w-full items-center">
                        <div>
                            <div className="mb-0 px-4 pt-4 border-0">
                                <div className="flex lg:flex-row flex-col">
                                    <div className="relative px-4 max-w-full">
                                        <h3 className="font-semibold text-lg text-gray-800">Electronic Housing Unit</h3>
                                    </div>
                                    <div className="relative px-4 max-w-full flex lg:flex-col text-sm lg:border-l-2">
                                        <span className="flex lg:mr-0 mr-1">Status:</span>
                                        <span className="flex text-indigo-600">{ehuData["status"]}</span>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <div className="rounded-t mb-0 pt-1 pb-4 px-4 border-0">
                                    <div className="relative px-4 max-w-full flex flex-row items-center">
                                        <IdentificationIcon className="h-5 w-5 text-gray-600" aria-hidden="true" />
                                        <span className="text-gray-500 px-2 text-sm">{id}</span>
                                    </div>
                                    <div className="relative px-4 max-w-full flex flex-row items-center">
                                        <FontAwesomeIcon
                                            icon={faBarcode}
                                            className="h-4 w-5 text-gray-600"
                                        />
                                        <span className="text-gray-500 px-2 text-sm">{ehuData["productCode"]}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="relative lg:flex mb-1 sm:mb-4">
                                <div className="flex flex-row mb-1 justify-between w-full lg:mr-2">
                                    <div className="mb-0 px-4 py-4 w-full">
                                        <InfoSection
                                            editableData={editableEhuData}
                                            editing={editing}
                                            toggleEditing={toggleEditing}
                                        ></InfoSection>
                                    </div>
                                </div>
                                <div className="flex flex-row justify-between w-full p-4">
                                    <div className="mb-0 px-4 border-0 w-full">
                                        <div className="flex flex-wrap items-center">
                                            <div className="flex flex-col w-full">
                                                <div className="relative w-full max-w-full flex-grow flex-1 mt-2 mb-4 flex justify-between border-b-2 border-gray-100 items-center">
                                                    <h3 className="font-semibold text-lg text-gray-800">Parts</h3>
                                                    {!showSelect ? (
                                                        <SquaresPlusIcon className="h-5 w-5 cursor-pointer text-gray-600 hover:text-green-400" onClick={() => setShowSelect(!showSelect)} />
                                                    ) : (
                                                        <div className="flex space-x-2">

                                                            <FontAwesomeIcon
                                                                icon={faRectangleXmark}
                                                                className="h-5 w-5 text-gray-600 hover:text-red-400 cursor-pointer"
                                                                onClick={() => setShowSelect(!showSelect)}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                                <div className={`w-full pb-2 ${showSelect ? "" : "hidden"}`}>
                                                    <PartsSelect classname="w-full" required={true} onChange={(part) => addPart(part)} />
                                                </div>
                                                {partError && (
                                                    <div className="w-full flex justify-center items-center mb-2">
                                                        <span className="text-red-600 bg-red-100 border border-red-300 rounded px-4 py-2 text-sm font-medium shadow-sm">
                                                            {partError.type} with serial number:{' '}
                                                            <span
                                                                onClick={() => navigate(`/main/part/${partError.partId}`)}
                                                                className="text-blue-600 underline cursor-pointer hover:text-blue-800"
                                                            >
                                                                {partError.serialNumber}
                                                            </span>{' '}
                                                            already has a connected Electronic Housing Unit, please disconnect this first.
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                            <div className='w-full border border-blue-100 rounded'>
                                                <div className="flex py-2 px-4 bg-blue-100/75 font-semibold">
                                                    <div className="flex-1 text-sm">Part</div>
                                                    <div className="flex-1 text-sm">Type</div>
                                                    <div className="flex-1 text-sm">Serial Number</div>
                                                    <div className="flex w-1/12 justify-center"></div>
                                                </div>
                                                <ul>
                                                    {
                                                        ehuPartsData.map((part, index) => {
                                                            // Determine the background color based on the index
                                                            const backgroundColor = index % 2 === 0 ? 'bg-white' : 'bg-blue-100/20';
                                                            const spec = specData?.find(e => e.id === parseInt(part["partSpecificationsId"]));
                                                            return (
                                                                <li key={part["id"]} className={`flex py-2 px-4 border-t border-blue-100 ${backgroundColor}`}>
                                                                    <div onClick={() => navigate(`/main/part/${part["id"]}`)} className="flex-1 text-sm hover:text-indigo-400 cursor-pointer font-semibold">{spec?.["partType"]}</div>
                                                                    <div className="flex-1 text-sm flex flex-col">
                                                                        <span>{spec?.["details"][0]["value"]}</span>
                                                                        <span>{spec?.["details"][1]?.["value"]}</span>
                                                                    </div>
                                                                    <div title={part["serialNumber"]} className="flex-1 text-sm whitespace-nowrap overflow-hidden text-ellipsis">{part["serialNumber"]}</div>
                                                                    <div className="flex w-1/12 justify-end items-center">
                                                                        <MinusCircleIcon
                                                                            className="h-5 w-5 text-red-600 cursor-pointer hover:text-red-400"
                                                                            onClick={() => removePart(part["id"])}
                                                                        />
                                                                    </div>
                                                                </li>
                                                            );
                                                        })
                                                    }
                                                </ul>
                                            </div>

                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-row mb-1 justify-between w-full items-center lg:mr-2 pb-4">
                                <div className="mb-0 px-4 py-4 border-0 w-full">
                                    <div className="flex flex-wrap items-center">
                                        <div className="relative w-full px-4 max-w-full flex-grow flex-1"><h3
                                            className="font-semibold text-lg text-gray-800 border-b-2 border-gray-100 py-2">Remarks</h3>
                                        </div>
                                    </div>
                                    <RemarkTextArea
                                        required
                                        onChange={e => newRemarkHandler(e)}
                                        value={newRemark}
                                        confirm={() => newRemarkRequestHandler()}
                                        cancel={() => newRemarkCancelHandler()} />
                                    {reverseArray(ehuData["remarks"])?.map((remark, index) => {
                                        if (!remark["text"].includes('certificateNumber'))
                                            return (
                                                <div key={remark["id"]} className="relative w-full px-4 py-4 max-w-full border-b border-gray-200">
                                                    <div className="flex justify-between items-start">
                                                        <div className="w-full">
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex items-center space-x-2">
                                                                    <span className="text-indigo-600 font-medium">{remark["name"]}</span>
                                                                    <span className="text-gray-400 text-sm">{moment(remark["on"]).format("DD-MM-yyyy")}</span>
                                                                </div>
                                                                {/* Edit/Delete Buttons — only if not deleted */}
                                                                {remark.removedOn === "0001-01-01T00:00:00+00:00" && (
                                                                    (remark["by"] === accountData["id"] || accountData.roles?.includes("administrator")) && (
                                                                        <div className="flex space-x-2">
                                                                            {remark["by"] === accountData["id"] && (
                                                                                <button
                                                                                    onClick={() => handleRemarkEdit(remark["id"], remark["text"])}
                                                                                    className="text-blue-500 hover:text-blue-700 text-sm"
                                                                                >
                                                                                    Edit
                                                                                </button>
                                                                            )}
                                                                            {(accountData.roles?.includes("administrator") || remark["by"] === accountData["id"]) && (
                                                                                <button
                                                                                    onClick={() => handleRemarkDelete(remark["id"])}
                                                                                    className="text-red-500 hover:text-red-700 text-sm"
                                                                                >
                                                                                    Delete
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    )
                                                                )}
                                                            </div>
                                                            {/* Remark Text or Deleted Notice */}
                                                            <div className="text-sm mt-2 text-gray-600">
                                                                {remark.removedOn !== "0001-01-01T00:00:00+00:00" && remark.deletedName ? (
                                                                    <div className="italic text-red-500">
                                                                        This remark was deleted by {remark.deletedName} on{" "}
                                                                        {moment(remark.removedOn).format("LLL")}
                                                                    </div>
                                                                ) : (
                                                                    <>
                                                                        {editRemark.id === remark["id"] ? <RemarkTextArea
                                                                            required
                                                                            onChange={e => newRemarkHandler(e)}
                                                                            value={editRemark?.text}
                                                                            confirm={() => newRemarkRequestHandler()}
                                                                            cancel={() => newRemarkCancelHandler()} /> : <pre className="whitespace-pre-wrap break-words text-sm text-gray-600 font-sans">{remark["text"]}</pre>}
                                                                        {/* Edited Info */}
                                                                        {remark.modifiedOn !== "0001-01-01T00:00:00+00:00" && remark.editedName && (
                                                                            <div className="text-xs text-gray-400 mt-1 italic">
                                                                                Edited by {remark.editedName} on {moment(remark.modifiedOn).format("LLL")}
                                                                            </div>
                                                                        )}
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                : null}
        </>
    )
}